import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography
} from '@mui/material';

import { logger } from '../../../utils/logger';
import {
  plcSectionsService,
  PlcSection,
  PlcSectionState,
  PLC_SECTIONS,
  PLC_SECTION_LABELS
} from '../services/PlcSectionsService';

const log = logger.getLogger('PlcSectionAccessPanel');

interface RowState {
  state: PlcSectionState;
  message: string;
}

type Rows = Record<PlcSection, RowState>;

const defaultRows = (): Rows =>
  PLC_SECTIONS.reduce((acc, section) => {
    acc[section] = { state: 'ENABLED', message: '' };
    return acc;
  }, {} as Rows);

interface PlcSectionAccessPanelProps {
  accountId: number;
  disabled?: boolean;
}

export interface PlcSectionAccessPanelHandle {
  /**
   * Persists staged section changes to plc-svc. Called from the page's submit flow.
   * Resolves when nothing is dirty or everything saved; throws an Error naming the
   * sections that failed (successes are still committed).
   */
  applySectionChanges: () => Promise<void>;
}

// Stages PLC section overrides for an account. Edits are local until the page's
// global Save Changes submits, which applies them per section (PUT/DELETE) via ref.
const PlcSectionAccessPanel = forwardRef<PlcSectionAccessPanelHandle, PlcSectionAccessPanelProps>(
  ({ accountId, disabled = false }, ref) => {
    const [saved, setSaved] = useState<Rows>(defaultRows);
    const [drafts, setDrafts] = useState<Rows>(defaultRows);
    const [loading, setLoading] = useState<boolean>(true);
    const [loaded, setLoaded] = useState<boolean>(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    useEffect(() => {
      const fetchSections = async () => {
        try {
          setLoading(true);
          setLoaded(false);
          const sections = await plcSectionsService.getSections(accountId);
          const rows = defaultRows();
          sections.forEach(({ section, state, message }) => {
            if (rows[section]) {
              rows[section] = { state, message: message || '' };
            }
          });
          setSaved(rows);
          setDrafts(rows);
          setLoadError(null);
          setLoaded(true);
        } catch (err) {
          log.error('Error fetching PLC section access:', err);
          setLoadError('Failed to load PLC section access. Please try again later.');
        } finally {
          setLoading(false);
        }
      };
      fetchSections();
    }, [accountId]);

    const updateDraft = useCallback((section: PlcSection, patch: Partial<RowState>) => {
      setDrafts(prev => ({ ...prev, [section]: { ...prev[section], ...patch } }));
    }, []);

    const isDirty = useCallback(
      (section: PlcSection): boolean =>
        drafts[section].state !== saved[section].state ||
        (drafts[section].state === 'DISABLED' &&
          drafts[section].message.trim() !== (saved[section].message || '').trim()),
      [drafts, saved]
    );

    useImperativeHandle(ref, () => ({
      applySectionChanges: async () => {
        // Never touch settings unless the initial GET succeeded — an API issue (or an
        // account without PLC access) must not block the account save or write overrides.
        if (!loaded) {
          return;
        }

        const dirtySections = PLC_SECTIONS.filter(isDirty);
        const failed: PlcSection[] = [];

        for (const section of dirtySections) {
          const draft = drafts[section];
          try {
            if (draft.state === 'ENABLED') {
              // Enabled is the default — clear the override instead of storing one
              await plcSectionsService.resetSection(accountId, section);
            } else {
              await plcSectionsService.setSection(
                accountId,
                section,
                draft.state,
                draft.state === 'DISABLED' ? draft.message : undefined
              );
            }
            const savedRow: RowState = {
              state: draft.state,
              message: draft.state === 'DISABLED' ? draft.message.trim() : ''
            };
            setSaved(prev => ({ ...prev, [section]: savedRow }));
            setDrafts(prev => ({ ...prev, [section]: savedRow }));
          } catch (err) {
            log.error(`Error saving PLC section ${section}:`, err);
            failed.push(section);
          }
        }

        if (failed.length > 0) {
          const labels = failed.map(section => PLC_SECTION_LABELS[section]).join(', ');
          throw new Error(
            `Failed to save PLC section changes for: ${labels}. Other changes were saved — please try again.`
          );
        }
      }
    }), [accountId, drafts, isDirty, loaded]);

    return (
      <Box>
        <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
          PLC Section Access
        </Typography>
        <Divider sx={{ mb: 2, width: '100%' }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Controls Program Leader Center sections for this account. Enabled: visible and usable
          (default). Disabled: grayed out in the leader's navigation, with an optional message shown
          on the page. Hidden: removed from the leader's navigation entirely. Changes are applied
          when you click Save Changes.
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {PLC_SECTIONS.map(section => {
              const draft = drafts[section];
              const dirty = isDirty(section);
              return (
                <Box
                  key={section}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 2,
                    p: 1.5,
                    border: '1px solid',
                    borderColor: dirty ? 'warning.light' : 'divider',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 140 }}>
                    {PLC_SECTION_LABELS[section]}
                  </Typography>

                  <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
                    <InputLabel id={`plc-section-${section}-label`}>State</InputLabel>
                    <Select
                      labelId={`plc-section-${section}-label`}
                      id={`plc-section-${section}`}
                      value={draft.state}
                      label="State"
                      onChange={(e) => updateDraft(section, { state: e.target.value as PlcSectionState })}
                    >
                      <MenuItem value="ENABLED">Enabled</MenuItem>
                      <MenuItem value="DISABLED">Disabled</MenuItem>
                      <MenuItem value="HIDDEN">Hidden</MenuItem>
                    </Select>
                  </FormControl>

                  {draft.state === 'DISABLED' && (
                    <TextField
                      size="small"
                      id={`plc-section-${section}-message`}
                      label="Message (optional)"
                      value={draft.message}
                      onChange={(e) => updateDraft(section, { message: e.target.value })}
                      disabled={disabled}
                      sx={{ flexGrow: 1, minWidth: 220 }}
                    />
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                    {dirty && (
                      <Chip label="Unsaved" size="small" color="warning" variant="outlined" />
                    )}
                    {draft.state !== 'ENABLED' && (
                      <Button
                        type="button"
                        size="small"
                        color="inherit"
                        onClick={() => updateDraft(section, { state: 'ENABLED', message: '' })}
                        disabled={disabled}
                      >
                        Reset to Default
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Box>
    );
  }
);

PlcSectionAccessPanel.displayName = 'PlcSectionAccessPanel';

export default PlcSectionAccessPanel;
